import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SuccessWareMasterFieldsService } from '../services/SuccessWareMasterFieldsService';
import type { SuccessWareMasterField } from '../repositories/SuccessWareMasterFieldsRepository';

const service = new SuccessWareMasterFieldsService();

// Query keys
export const successWareMasterFieldsKeys = {
  all: ['successware-master-fields'] as const,
  lists: () => [...successWareMasterFieldsKeys.all, 'list'] as const,
  list: (filters: string) => [...successWareMasterFieldsKeys.lists(), { filters }] as const,
  details: () => [...successWareMasterFieldsKeys.all, 'detail'] as const,
  detail: (id: string) => [...successWareMasterFieldsKeys.details(), id] as const,
  entityTypes: () => [...successWareMasterFieldsKeys.all, 'entity-types'] as const,
  stats: () => [...successWareMasterFieldsKeys.all, 'stats'] as const,
  byEntity: (entityType: string) => [...successWareMasterFieldsKeys.all, 'by-entity', entityType] as const,
  search: (searchText: string, entityType?: string) => 
    [...successWareMasterFieldsKeys.all, 'search', { searchText, entityType }] as const,
};

/**
 * Hook to get all SuccessWare master fields with optional entity type filter
 */
export function useSuccessWareMasterFields(entityType?: string) {
  return useQuery({
    queryKey: successWareMasterFieldsKeys.list(entityType || 'all'),
    queryFn: () => service.getAllFields(entityType),
    staleTime: 5 * 60 * 1000, // 5 minutes - field mappings rarely change
  });
}

/**
 * Hook to get fields for a specific entity type
 */
export function useFieldsByEntity(entityType: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: successWareMasterFieldsKeys.byEntity(entityType),
    queryFn: () => service.getFieldsByEntity(entityType),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  });
}

/**
 * Hook to search fields
 */
export function useSearchFields(searchText: string, entityType?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: successWareMasterFieldsKeys.search(searchText, entityType),
    queryFn: () => service.searchFields(searchText, entityType),
    staleTime: 2 * 60 * 1000, // 2 minutes for search results
    enabled: options?.enabled !== false && searchText.length > 0,
  });
}

/**
 * Hook to get unique entity types
 */
export function useEntityTypes() {
  return useQuery({
    queryKey: successWareMasterFieldsKeys.entityTypes(),
    queryFn: () => service.getEntityTypes(),
    staleTime: 10 * 60 * 1000, // 10 minutes - entity types rarely change
  });
}

/**
 * Hook to get field statistics by entity type
 */
export function useFieldStats() {
  return useQuery({
    queryKey: successWareMasterFieldsKeys.stats(),
    queryFn: () => service.getFieldStats(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to get only imported fields
 */
export function useImportedFields(entityType?: string) {
  return useQuery({
    queryKey: [...successWareMasterFieldsKeys.all, 'imported', entityType || 'all'],
    queryFn: () => service.getImportedFields(entityType),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to update field description or notes
 */
export function useUpdateField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Pick<SuccessWareMasterField, 'description' | 'notes'> }) =>
      service.updateField(id, updates),
    onSuccess: (updatedField) => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: successWareMasterFieldsKeys.all });
      
      // Optionally update the cache directly for instant feedback
      queryClient.setQueryData<SuccessWareMasterField[]>(
        successWareMasterFieldsKeys.list('all'),
        (old) => {
          if (!old) return old;
          return old.map((field) => (field.id === updatedField.id ? updatedField : field));
        }
      );

      // Update entity-specific cache if it exists
      queryClient.setQueryData<SuccessWareMasterField[]>(
        successWareMasterFieldsKeys.byEntity(updatedField.entity_type),
        (old) => {
          if (!old) return old;
          return old.map((field) => (field.id === updatedField.id ? updatedField : field));
        }
      );
    },
  });
}
